import { useEffect, useMemo, useState } from "react";
import BikeCard from "@/components/BikeCard";
import { fetchBikes } from "@/lib/api";

const CATEGORIES = [
  { value: "All", label: "Όλες" },
  { value: "SuperSport", label: "SuperSport" },
  { value: "Naked", label: "Naked" },
  { value: "Adventure", label: "Adventure" },
  { value: "Cafe Racer", label: "Cafe Racer" },
];
const CONDITIONS = [
  { value: "All", label: "Όλες" },
  { value: "New", label: "Καινούργιες" },
  { value: "Used", label: "Μεταχειρισμένες" },
];

export default function Catalog({
  defaultCondition = "All",
  collectionFilter = null,
  overline = "Η σειρά",
  titleLine1 = "Ο κατάλογος.",
  titleAccent = "Διάλεξε το όπλο σου.",
  subtitle = "Κάθε μηχανή στο showroom είναι επιλεγμένη στο χέρι, πλήρως ελεγμένη και έτοιμη να βγει έξω. Καινούργιες, χαμηλά χιλιόμετρα και καμιά φορά κανένα μονόκερο.",
} = {}) {
  const [bikes, setBikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [condition, setCondition] = useState(defaultCondition);
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    setLoading(true);
    const params = collectionFilter ? { collection: collectionFilter } : {};
    fetchBikes(params)
      .then(setBikes)
      .catch(() => setBikes([]))
      .finally(() => setLoading(false));
  }, [collectionFilter]);

  const filtered = useMemo(() => {
    let list = [...bikes];
    if (category !== "All") list = list.filter((b) => b.category === category);
    if (condition !== "All") list = list.filter((b) => b.condition === condition);
    if (sort === "price_asc") list.sort((a, b) => a.price - b.price);
    if (sort === "price_desc") list.sort((a, b) => b.price - a.price);
    if (sort === "year_desc") list.sort((a, b) => b.year - a.year);
    return list;
  }, [bikes, category, condition, sort]);

  return (
    <div data-testid="catalog-page" className="pt-40 md:pt-52 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 items-end gap-8 mb-12">
          <div className="md:col-span-7">
            <p className="text-overline mb-4">{overline}</p>
            <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-7xl leading-[0.9]">
              {titleLine1}<br/>
              <span className="text-[#E50000]">{titleAccent}</span>
            </h1>
          </div>
          <div className="md:col-span-5 text-white/60 text-sm md:text-base leading-relaxed">
            {subtitle}
          </div>
        </div>

        {/* Filters */}
        <div data-testid="catalog-filters" className="flex flex-col gap-4 md:flex-row md:items-center justify-between border-y border-white/10 py-4 mb-10">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.value}
                data-testid={`filter-cat-${c.value.toLowerCase().replace(' ', '-')}`}
                onClick={() => setCategory(c.value)}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border transition-colors ${
                  category === c.value
                    ? "bg-[#E50000] border-[#E50000] text-white"
                    : "bg-transparent border-white/15 text-white/70 hover:border-white/40"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            <select
              data-testid="filter-condition-select"
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
              className="bg-[#141414] border border-white/15 text-white text-xs uppercase tracking-wider px-3 py-2 focus:border-[#E50000] outline-none"
            >
              {CONDITIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <select
              data-testid="filter-sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-[#141414] border border-white/15 text-white text-xs uppercase tracking-wider px-3 py-2 focus:border-[#E50000] outline-none"
            >
              <option value="featured">Ταξινόμηση: Επιλεγμένα</option>
              <option value="price_asc">Τιμή: Αύξουσα</option>
              <option value="price_desc">Τιμή: Φθίνουσα</option>
              <option value="year_desc">Νεότερα Πρώτα</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0,1,2,3,4,5].map(i => <div key={i} className="aspect-[4/3] bg-[#141414] animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="catalog-empty" className="py-24 text-center text-white/50 border border-white/10">
            <p className="font-display uppercase tracking-tight text-2xl">Καμία μηχανή δεν ταιριάζει στα φίλτρα.</p>
            <p className="mt-2 text-sm">Δοκίμασε διαφορετική κατηγορία ή κατάσταση.</p>
          </div>
        ) : (
          <div data-testid="catalog-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((b, i) => <BikeCard key={b.id} bike={b} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
